<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    :nudge-right="40"
    transition="scale-transition"
    offset-y
    min-width="auto"
    :disabled="readonly"
    :left="left"
  >
    <template #activator="{ attrs }">
      <v-text-field
        v-bind="attrs"
        ref="input"
        :value="formattedDate"
        :label="label"
        :hide-details="hideDetails"
        :dense="dense"
        :solo="solo"
        :single-line="singleLine"
        :clearable="clearable"
        :rules="rules"
        :disabled="disabled"
        :readonly="readonly"
        :rounded="rounded"
        :persistent-placeholder="persistentPlaceholder"
        :hint="hint"
        :class="childClass"
        :persistent-hint="persistentHint"
        :placeholder="placeholder"
        class="v-text-field_date-input"
        :style="childStyle"
        data-test-tooltip="date-input"
        @click="onClick"
        @click:clear="onClear"
        @change="onInputChange"
        @keypress.enter.stop="onEnter"
      >
        <template v-if="prependIcon" #prepend>
          <v-icon data-test-tooltip="date-input-button" small @click="clickPrepend">{{ prependIcon }}</v-icon>
        </template>
        <template v-if="appendIcon" #append>
          <v-icon class="date-input-append-inner-icon" @click="clickAppend">
            {{ appendIcon }}
          </v-icon>
        </template>
      </v-text-field>
    </template>
    <div v-if="!useSystemDate">
      <v-date-picker
        ref="picker"
        :value="formattedDate"
        :locale="locale"
        :min="min"
        :max="max"
        now
        :first-day-of-week="1"
        :show-adjacent-months="showAdjacentMonths"
        :clickable-adjacent-months="clickableAdjacentMonths"
        data-test-tooltip="date-picker"
        @change="onPickerChange"
      />
    </div>
    <div v-else>
      <v-date-picker
        ref="picker"
        :value="formattedDate"
        :locale="locale"
        :min="min"
        :max="max"
        now
        :first-day-of-week="1"
        :show-adjacent-months="showAdjacentMonths"
        :clickable-adjacent-months="clickableAdjacentMonths"
        data-test-tooltip="date-picker"
        :show-current="getCurrent"
        @change="onPickerChange"
      />
    </div>
  </v-menu>
</template>

<script lang="ts">
import "reflect-metadata";
import { Component, Vue, PropSync, Prop, Watch } from "vue-property-decorator";
import { InputValidationRule } from "vuetify";
import { Validatable } from "@/models/components/inputs/validatable";

@Component
export default class InputDatePicker extends Vue {
  private menu = false;
  @PropSync("value", { required: true })
  modelValue?: string | null;
  @Prop({ default: "Date" })
  label?: string;
  @Prop({ default: "mdi-calendar" })
  prependIcon?: string;
  @Prop({ default: undefined })
  appendIcon?: string;
  @Prop({ default: false })
  hideDetails?: string | boolean;
  @Prop({ default: false })
  dense?: boolean;
  @Prop({ default: false })
  solo?: boolean;
  @Prop({ default: false })
  singleLine?: boolean;
  @Prop({ default: () => [] })
  rules?: Array<InputValidationRule>;
  @Prop({ default: undefined })
  min?: string;
  @Prop({ default: undefined })
  max?: string;
  @Prop()
  disabled?: boolean;
  @Prop()
  readonly?: boolean;
  @Prop()
  rounded?: boolean;
  @Prop()
  persistentPlaceholder?: boolean;
  @Prop()
  hint?: string;
  @Prop({ default: false })
  persistentHint?: boolean;
  @Prop()
  placeholder?: string;

  @Prop({ default: false })
  clearable?: boolean;

  @Prop({ default: false })
  left?: boolean;

  @Prop()
  childClass?: string;

  @Prop()
  childStyle?: string;

  @Prop({ default: true })
  openOnInputFocus!: boolean;

  @Prop({ default: true })
  showAdjacentMonths!: boolean;
  @Prop({ default: true })
  clickableAdjacentMonths!: boolean;

  @Prop({ default: false })
  useSystemDate!: boolean;

  //Value to save initial value of input
  initialValue = "";
  inputValue = "";

  get formattedDate(): string | null {
    return this.$options.filters?.formatDate(this.inputValue);
  }

  get locale(): string {
    return this.$i18n.locale;
  }

  get systemDate(): string {
    return this.$store.direct.state.user.officeSettings?.userSystemDate ?? this.inputValue;
  }

  get getCurrent() {
    return this.systemDate.split(/[-T:]/).slice(0, 3).join("-");
  }

  validate(force = false): boolean {
    const component = this.$refs["input"] as Vue & Validatable;
    return component.validate(force);
  }

  //Parse date to format YYYY-MM-DD
  parseDate(date: string | null | undefined): string | null {
    if (!date) return null;

    let [year, month, day] = date.split("-");
    let [yearN, monthN, dayN] = date.split("-").map((x) => Number.parseInt(x));

    if (year?.length != 4 || month?.length != 2 || day?.length != 2) {
      return this.$options.filters?.formatDate(this.initialValue);
    }

    const canProcessDate = Number.isInteger(yearN) && Number.isInteger(monthN) && Number.isInteger(dayN);
    if (!canProcessDate) {
      return this.$options.filters?.formatDate(this.initialValue);
    }

    if (monthN < 1 || monthN > 12) {
      return this.$options.filters?.formatDate(this.initialValue);
    }

    const lastDayOfMonth = new Date(yearN, monthN, 0).getDate();
    if (dayN < 1 || dayN > lastDayOfMonth) {
      return this.$options.filters?.formatDate(this.initialValue);
    }

    return this.$options.filters?.formatDate(`${year}-${month}-${day}`);
  }

  setValue(value: string | null) {
    const date = this.parseDate(value);
    this.setInputValue(date);
    this.setModelValue(date);
  }

  setModelValue(value: string | null) {
    if (value && value.length > 1) {
      this.modelValue = value; //To convert value to ISO without timezone
    } else {
      this.modelValue = this.clearable ? null : this.initialValue;
    }
  }

  setInputValue(value: string | null) {
    this.inputValue = null as unknown as string;

    setTimeout(() => {
      if (value && value.length > 1) {
        this.inputValue = value;
      } else {
        this.input?.reset();
        this.inputValue = this.clearable ? "" : this.initialValue;
      }
    }, 50);
  }

  onClick() {
    if (this.openOnInputFocus) this.menu = !this.menu;

    if (!this.menu && this.useSystemDate) {
      const picker = this.$refs.picker as HTMLFormElement & Vue;
      picker.tableDate = this.getCurrent; // to reset table selected date (return to current selected by user)
    }
  }

  async clickPrepend() {
    if (this.disabled) return;
    await this.$store.direct.dispatch.waitForCompleteAllRequests(); // #1318 - re rendering list cause not showing date picker after change another request
    this.menu = true;
  }

  async clickAppend() {
    if (this.disabled) return;
    await this.$store.direct.dispatch.waitForCompleteAllRequests(); // #1318 - re rendering list cause not showing date picker after change another request
    this.menu = true;
  }

  onInputBlur(event: MouseEvent) {
    this.input?.reset();
    this.onInputChange((event.target as HTMLInputElement).value);
  }

  onInputChange(value: string | null) {
    this.menu = false;
    this.setValue(value);
  }

  onPickerChange(value: string | null) {
    this.menu = false;
    this.setValue(value);
  }

  onEnter() {
    this.menu = false;

    setTimeout(() => {
      (this.$refs.input as HTMLFormElement).blur();
      this.$emit("filter");
    }, 100);
  }

  onClear() {
    this.input?.blur();
    this.input?.reset();
    this.setValue("");
    this.onEnter();
  }

  @Watch("modelValue")
  setInitialModelValue(value: string) {
    this.setInputValue(value);
  }

  mounted() {
    this.inputValue = this.modelValue as string;
    this.initialValue = this.modelValue as string;
    this.input = this.$refs.input as HTMLFormElement & Vue;
    this.picker = this.$refs.picker as HTMLFormElement & Vue;
  }

  input?: HTMLFormElement & Vue = undefined;
  picker?: HTMLFormElement & Vue = undefined;
}
</script>

<style>
.date-input-append-inner-icon svg {
  height: 18px !important;
  width: 18px !important;
}
button.date-input-append-inner-icon {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  margin-top: 2px;
}

.prepend-icon-size {
  font-size: 22px !important;
}

.rounded-icon-margin {
  margin: -2px 0 0;
}
.v-input__prepend-outer {
  padding: 1px;
  margin-left: 6px !important;
  margin-right: 6px !important;
}
</style>
